import {Properties} from 'csstype'
import React from 'react'

export type FontStyleProperties = Pick<
  Properties,
  'fontFamily' | 'fontWeight' | 'fontSize' | 'lineHeight' | 'letterSpacing' | 'textTransform'
>

const fontThemeNames = [
  'largeTitle',
  'largeTitleBold',
  'mediumTitle',
  'mediumTitleBold',
  'smallTitleBold',
  'title',
  'titleBold',
  'body',
  'bodyMedium',
  'bodySemiBold',
  'bodyBold',
  'subhead',
  'subheadBold',
  'caption',
  'captionBold',
  'captionSerif',
  'tiny',
  'micro'
] as const

export type FontTheme = Record<(typeof fontThemeNames)[number], FontStyleProperties>

export const FontContext = React.createContext<FontTheme>({
  largeTitle: {},
  largeTitleBold: {},
  mediumTitle: {},
  mediumTitleBold: {},
  smallTitleBold: {},
  title: {},
  titleBold: {},
  body: {},
  bodyMedium: {},
  bodySemiBold: {},
  bodyBold: {},
  subhead: {},
  subheadBold: {},
  caption: {},
  captionBold: {},
  captionSerif: {},
  tiny: {},
  micro: {}
})

/**
 * Font styles as defined in our design system.
 *
 * Services that set their titles in a display face pass it as `titleFontFamily`;
 * the rest render titles in the body face.
 */
export const fontStyleGenerator = (
  proximaFontFamily: string,
  titleFontFamily: string = proximaFontFamily
): FontTheme => ({
  largeTitle: {fontSize: '34px', fontFamily: titleFontFamily},
  largeTitleBold: {fontSize: '34px', fontFamily: titleFontFamily, fontWeight: 700},
  mediumTitle: {fontSize: '28px', fontFamily: titleFontFamily},
  mediumTitleBold: {fontSize: '28px', fontFamily: titleFontFamily, fontWeight: 700},
  smallTitleBold: {fontSize: '22px', fontFamily: proximaFontFamily, fontWeight: 700},
  title: {fontSize: '17px', fontFamily: titleFontFamily},
  titleBold: {fontSize: '17px', fontFamily: titleFontFamily, fontWeight: 700},
  body: {fontSize: '17px', fontFamily: proximaFontFamily},
  bodyMedium: {fontSize: '17px', fontFamily: proximaFontFamily, fontWeight: 500},
  bodySemiBold: {fontSize: '17px', fontFamily: proximaFontFamily, fontWeight: 600},
  bodyBold: {fontSize: '17px', fontFamily: proximaFontFamily, fontWeight: 700},
  subhead: {fontSize: '15px'},
  subheadBold: {fontSize: '15px', fontFamily: proximaFontFamily, fontWeight: 700},
  caption: {fontSize: '12px'},
  captionBold: {fontSize: '12px', fontFamily: proximaFontFamily, fontWeight: 700},
  captionSerif: {fontSize: '12px', fontFamily: titleFontFamily},
  tiny: {fontSize: '11px', lineHeight: '13px'},
  micro: {fontSize: '9px'}
})
